import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { ChevronRight, Sparkles, Trash2 } from "lucide-react";
import OrderTimeline from "../../components/orders/OrderTimeline";
import { nextStatus } from "./_shared";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const reload = useCallback(
    () => adminApi().get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const updateStatus = async (id, newStatus) => {
    try { await adminApi().patch(`/admin/orders/${id}`, { status: newStatus }); toast.success(`Marked as ${newStatus}`); reload(); }
    catch { toast.error("Update failed"); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Move order #${o.id.slice(0, 8).toUpperCase()} to Recently Deleted?\n\nYou can restore it within 30 days from the Recently Deleted tab.`)) return;
    try {
      await adminApi().delete(`/admin/orders/${o.id}`);
      toast.success(`Order moved to Recently Deleted`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="admin-orders-tab">
      {orders.length === 0 && <p className="text-[#5C5555]">No orders yet.</p>}
      {orders.map((o) => {
        const nxt = nextStatus(o.status);
        return (
          <div key={o.id} className="bg-white border border-[#E5DBD8] p-4 text-sm">
            <div className="flex justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-serif text-lg text-[#7A1F2B]">#{o.id.slice(0, 8)}</p>
                  {o._customer_loyalty_awarded && (
                    <span
                      data-testid={`order-loyalty-badge-${o.id}`}
                      title={o._customer_loyalty_code ? `Loyalty code: ${o._customer_loyalty_code} — send a WhatsApp thank-you!` : "Customer has hit their 3-order loyalty milestone — send a WhatsApp thank-you!"}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFF7DC] border border-[#E08036] text-[10px] uppercase tracking-wider text-[#7A1F2B] rounded-full -rotate-2 font-hand"
                      style={{ boxShadow: "2px 2px 0 #E08036" }}
                    >
                      <Sparkles className="w-3 h-3"/> Loyalty Awarded
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#5C5555]">{new Date(o.created_at).toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="font-serif text-lg">₹{o.total.toLocaleString("en-IN")}</p>
                <span className={`text-xs uppercase tracking-wider ${o.status === "paid" ? "text-green-700" : "text-amber-700"}`}>{o.status}</span>
              </div>
            </div>
            <p className="mt-2"><b>{o.customer?.name}</b> · {o.customer?.phone} · {o.customer?.email}</p>
            <p className="text-[#5C5555]">{o.customer?.address}, {o.customer?.city} - {o.customer?.pincode}</p>
            <p className="text-[#5C5555] mt-1">{o.items?.map((it) => `${it.name}×${it.quantity}`).join(", ")}</p>
            {o.items?.some((it) => it.note) && (
              <div className="mt-2 space-y-1" data-testid={`order-notes-${o.id}`}>
                {o.items.filter((it) => it.note).map((it, idx) => (
                  <div key={idx} className="text-xs bg-[#FFFAF3] border border-dashed border-[#7A1F2B] px-2 py-1 rounded italic">
                    💌 <b>{it.name}:</b> &ldquo;{it.note}&rdquo;
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              <OrderTimeline status={o.status} compact/>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider text-[#5C5555]">Status:</span>
              <select
                value={o.status}
                onChange={(e) => updateStatus(o.id, e.target.value)}
                data-testid={`order-status-${o.id}`}
                className="border border-[#E5DBD8] px-3 py-1 text-sm bg-white rounded"
              >
                {["pending", "paid", "packed", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {nxt && (
                <button
                  onClick={() => updateStatus(o.id, nxt)}
                  data-testid={`order-advance-${o.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-hand bg-[#7A1F2B] text-white rounded-full hover:bg-[#5C1520]"
                >
                  Mark as {nxt} <ChevronRight className="w-3 h-3"/>
                </button>
              )}
              <button
                onClick={() => deleteOrder(o)}
                data-testid={`order-delete-${o.id}`}
                className="ml-auto inline-flex items-center gap-1 px-3 py-1 text-xs font-hand bg-white border border-[#7A1F2B] text-[#7A1F2B] rounded-full hover:bg-[#FFE7F1]"
                title="Move to Recently Deleted"
              >
                <Trash2 className="w-3 h-3"/> Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
